import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { find, intersection, isNil, map, size, toLower } from 'lodash'
import PropTypes from 'prop-types'

import { areArraysIdentical } from '../../../core/utils'

import { Select } from '../../../common/components/sections'
import { StyledEditabledSelectContainer } from '../../../common/components/styled'
import { HOTKEYS_ENABLED_TAGS } from '../../../common/constants'

import { DISH_FIELDS_CONFIG, DISHES_AVAILABLE_HOTKEYS } from '../../constants'
import {
  useDishEditableCellHotkey,
  useDishSuggestCurationAndUpdate,
  useUpdateDishMutation,
} from '../../hooks'
import { dishPropType } from '../../prop-types'
import { transformDishToUpdateArgs } from '../../transformers'

const DishEditableSelectCell = ({
  fieldName,
  value,
  options,
  defaultComponent,
  isDisabled,
  dish,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [selectedValue, setSelectedValue] = useState([])
  const selectRef = useRef(null)
  const [updateDish] = useUpdateDishMutation(dish?.id)
  const [suggestAndUpdate] = useDishSuggestCurationAndUpdate(dish.id)

  const { isSelectInHotkeyEditMode, setHotkeyEditModeEnabled } =
    useDishEditableCellHotkey(dish.id, fieldName)

  useEffect(() => {
    if (!isNil(selectRef.current)) {
      selectRef.current.focus()
    }
  }, [isEditing])

  useEffect(() => {
    setSelectedValue(value)
  }, [dish.id, value])

  const handleClick = useCallback(
    e => {
      e.stopPropagation()
      if (!isDisabled) {
        setIsEditing(true)
      }
    },
    [isDisabled],
  )

  const handleBlur = useCallback(() => {
    if (!areArraysIdentical(selectedValue, value)) {
      if (fieldName === DISH_FIELDS_CONFIG.DIETS.value) {
        suggestAndUpdate(
          { shouldSuggestOnlyIngredients: true },
          { [fieldName]: selectedValue },
        )
      } else {
        updateDish(transformDishToUpdateArgs({ [fieldName]: selectedValue }))
      }
    }
    setIsEditing(false)
    setHotkeyEditModeEnabled(false)
  }, [
    selectedValue,
    value,
    setHotkeyEditModeEnabled,
    fieldName,
    suggestAndUpdate,
    updateDish,
  ])

  useEffect(() => {
    if (isSelectInHotkeyEditMode) {
      setIsEditing(isSelectInHotkeyEditMode)
    }
  }, [isSelectInHotkeyEditMode])

  useHotkeys(
    DISHES_AVAILABLE_HOTKEYS.ESC.hotkey,
    handleBlur,
    {
      enabled: isEditing,
      enableOnTags: HOTKEYS_ENABLED_TAGS,
    },
    [isEditing, handleBlur],
  )

  const handleDeselect = useCallback(() => {
    selectRef.current.focus()
  }, [selectRef])

  const handleChange = useCallback(
    newValues => {
      if (fieldName === DISH_FIELDS_CONFIG.DIETS.value) {
        const dietNames = map(newValues, id =>
          toLower(find(options, option => option.value === id)?.label),
        )
        if (size(intersection(dietNames, ['vegan', 'vegetarian'])) === 2) {
          return null
        }
      }

      return setSelectedValue(newValues)
    },
    [fieldName, options],
  )

  return (
    <StyledEditabledSelectContainer
      onClick={handleClick}
      role="presentation"
      isDisabled={isDisabled}
    >
      {isEditing && !isDisabled ? (
        <Select
          ref={selectRef}
          showSearch
          allowClear
          mode="multiple"
          onChange={handleChange}
          onDeselect={handleDeselect}
          onBlur={handleBlur}
          options={options}
          value={selectedValue}
          dropdownStyle={{ maxHeight: 400, minWidth: 250 }}
          dropdownClassName="dark-dropdown-highlights"
        />
      ) : (
        defaultComponent
      )}
    </StyledEditabledSelectContainer>
  )
}

DishEditableSelectCell.propTypes = {
  fieldName: PropTypes.string.isRequired,
  defaultComponent: PropTypes.node.isRequired,
  value: PropTypes.arrayOf(PropTypes.string),
  isDisabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.object),
  dish: dishPropType.isRequired,
}

DishEditableSelectCell.defaultProps = {
  isDisabled: false,
  value: [],
  options: [],
}

export default DishEditableSelectCell
